import React, { useState, useMemo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme, APP_THEMES } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { usePro, PRO_FEATURES } from '../contexts/ProContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { ScrollArea } from './ui/scroll-area';
import { languages } from '../i18n';
import FloatingActionButton from './FloatingActionButton';
import { AdManager } from './AdBanner';
// Import shared navigation components
import { getGlobalNavItems, getKidsNavItems } from './navigation';
import {
  BookOpen,
  Compass,
  MapPin,
  Clock,
  Settings,
  Bookmark,
  Menu,
  Moon,
  Sun,
  BookText,
  X,
  HandHeart,
  Globe,
  ZoomIn,
  Search,
  Crown,
  TrendingUp,
  Scan,
  Sparkles,
  Download,
  Home,
  Radio,
  GraduationCap,
  Mic,
  Star,
  Palette,
  Check,
} from 'lucide-react';

const Layout = () => {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme, appTheme, setAppTheme, mode, themes } = useTheme();
  const { settings } = useSettings();
  const { isPro, promptUpgrade, requestFeatureAccess, isFeatureUnlockedViaAd } = usePro();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showMobileLanguageMenu, setShowMobileLanguageMenu] = useState(false);
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [showUstadhModal, setShowUstadhModal] = useState(false);

  // Pro-gated Night Vision toggle - STRICT PRO ONLY (NO AD UNLOCK)
  const handleNightVisionToggle = () => {
    // If currently in light mode, switching to dark requires Pro (NO ad unlock)
    if (mode === 'light') {
      if (!isPro) {
        // Night Vision is PRO ONLY - show upgrade prompt, no ad option
        requestFeatureAccess({ 
          ...PRO_FEATURES.NIGHT_VISION,
          noAdUnlock: true // Signal that ad unlock is not available
        });
        return;
      }
    }
    // If in dark mode, can always switch back to light (free)
    toggleTheme();
  };

  // Theme-specific styles for mobile sidebar
  const getSidebarStyles = () => {
    if (appTheme === 'emerald') {
      return {
        container: 'bg-gradient-to-b from-[#0A3022] to-[#0B3B2E] border-l-[#CBB373]/40',
        header: 'bg-[#0B3B2E]',
        headerText: 'text-[#E9E1CD]',
        headerTagline: 'text-[#E7DAB4]/70',
        navLink: 'text-white',  // WHITE text for nav items
        navLinkActive: 'bg-gradient-to-r from-[#CBB373] to-[#D8C68C] text-[#0A3022]',
        navLinkHover: 'hover:bg-[#CBB373]/15',
        footer: 'bg-[#124332] border-[#CBB373]/30',
        footerText: 'text-[#E7DAB4]',
      };
    }
    if (appTheme === 'royal') {
      return {
        container: 'bg-gradient-to-b from-[#0f0f1a] via-[#1a1a2e] to-[#16213e] border-l border-l-[#c9a227]/30',
        header: 'bg-gradient-to-r from-[#1a1a2e] to-[#0f0f1a]',
        headerText: 'text-white',
        headerTagline: 'text-[#c9a227]',
        navLink: 'text-white/90',
        navLinkActive: 'bg-gradient-to-r from-[#7c3aed] via-[#8b5cf6] to-[#7c3aed] text-white shadow-[0_0_20px_rgba(139,92,246,0.3)]',
        navLinkHover: 'hover:bg-[#c9a227]/10',
        footer: 'bg-gradient-to-r from-[#7c3aed]/10 to-[#c9a227]/10 border-t border-[#c9a227]/20',
        footerText: 'text-white/80',
      };
    }
    // Kids Paradise - Magical dreamy menu
    if (appTheme === 'kids') {
      return {
        container: 'border-l-0',
        containerStyle: {
          background: 'linear-gradient(180deg, #fce7f3 0%, #e9d5ff 30%, #dbeafe 60%, #cffafe 100%)',
        },
        header: '',
        headerStyle: {
          background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 50%, #06b6d4 100%)',
        },
        headerText: 'text-white',
        headerTagline: 'text-white/90',
        navLink: 'text-purple-800',
        navLinkActive: 'text-white shadow-lg',
        navLinkActiveStyle: {
          background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)',
          boxShadow: '0 4px 15px rgba(168, 85, 247, 0.4)',
        },
        navLinkHover: 'hover:bg-pink-100/50',
        footer: 'bg-gradient-to-r from-pink-100 to-purple-100',
        footerText: 'text-purple-600',
        isKids: true,
      };
    }
    // Classic theme - Cream & Gold
    if (appTheme === 'classic') {
      return {
        container: 'bg-[#F1EBDD] border-l border-l-[#C9A227]/40',
        header: 'bg-[#0F4C3A]',
        headerText: 'text-[#F1EBDD]',
        headerTagline: 'text-[#C9A227]',
        navLink: 'text-[#0F4C3A]',
        navLinkActive: 'bg-[#0F4C3A] text-[#C9A227] shadow-md',
        navLinkHover: 'hover:bg-[#C9A227]/15',
        footer: 'bg-[#C9A227]/10 border-t border-[#C9A227]/30',
        footerText: 'text-[#0F4C3A]',
      };
    }
    return {
      container: 'bg-gradient-to-b from-card to-card/95 backdrop-blur-xl border-l-0',
      header: 'bg-primary',
      headerText: 'text-white',
      headerTagline: 'text-white/70',
      navLink: '',
      navLinkActive: 'bg-primary text-primary-foreground',
      navLinkHover: 'hover:bg-muted',
      footer: 'bg-primary/5 border-primary/20',
      footerText: 'text-muted-foreground',
    };
  };

  const sidebarStyles = getSidebarStyles();

  // Check if we're viewing a specific surah (not the list, not zoom, not mushaf)
  const surahMatch = location.pathname.match(/^\/quran\/(\d+)$/);
  const currentSurahNumber = surahMatch ? surahMatch[1] : null;

  // Memoize navItems using shared navigation config
  // STANDARDIZATION MANDATE: Uses getGlobalNavItems or getKidsNavItems
  // Tajweed Coach positioned BELOW Masjid Guide in all non-kids themes
  const navItems = useMemo(() => {
    if (appTheme === 'kids') {
      return getKidsNavItems(t);
    }
    return getGlobalNavItems(t, appTheme);
  }, [t, appTheme]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/quran') return location.pathname === '/quran' || location.pathname.match(/^\/quran\/\d+$/);
    if (path.includes('/zoom/')) return location.pathname.includes('/zoom');
    return location.pathname.startsWith(path);
  };

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setShowLanguageMenu(false);
    // Update document direction for RTL languages
    const lang = languages.find(l => l.code === langCode);
    document.documentElement.dir = lang?.rtl ? 'rtl' : 'ltr';
  };

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];
  const showsMobileHeader = appTheme !== 'royal'
    && (location.pathname === '/' || location.pathname.startsWith('/quran'));

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        {/* Premium Desktop Sidebar */}
        <aside className={`fixed left-0 top-0 z-40 hidden h-screen w-72 border-r border-border/50 backdrop-blur-xl lg:block ${
          appTheme === 'emerald' ? 'emerald-desktop-sidebar' : 'bg-card/50'
        }`}>
          <div className="flex h-full flex-col">
            {/* Tanzeen + with Logo */}
            <div className="flex h-28 items-center px-6 border-b border-border/30">
              <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
                <img 
                  src={((appTheme === 'classic' || appTheme === 'kids') && mode === 'light') 
                    ? "/images/tanzeel-logo-black.png" 
                    : "/images/tanzeel-logo.png"
                  }
                  alt="Tanzeen +" 
                  className="h-10 w-10 object-contain"
                />
                <div>
                  <h1 className="font-serif text-2xl font-bold tracking-tight text-foreground">
                    Tanzeen <span className="font-black" style={{fontWeight: 900, WebkitTextStroke: "1px currentColor"}}>+</span>
                  </h1>
                  <p className="text-xs italic text-muted-foreground">{t('app.tagline')}</p>
                </div>
              </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 space-y-1 overflow-y-auto px-4 py-6">
              {navItems.map((item, index) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                
                // Emerald theme specific styles for desktop sidebar
                const emeraldNavClass = appTheme === 'emerald'
                  ? (active 
                      ? 'bg-gradient-to-r from-[#c9a227] to-[#d4b030] text-black shadow-lg' 
                      : 'text-white/90 hover:bg-[#c9a227]/20 hover:text-white')
                  : (active
                      ? 'bg-primary text-primary-foreground shadow-lg'
                      : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground');
                
                // Special highlight for Halal Scanner
                const highlightClass = item.isHighlight && !active
                  ? 'ring-2 ring-emerald-500/50 bg-emerald-500/10'
                  : '';
                
                // Handle Ustadh click - opens modal instead of navigating
                const handleNavClick = (e) => {
                  if (item.isUstadh) {
                    e.preventDefault();
                    if (isPro) {
                      setShowUstadhModal(true);
                    } else {
                      requestFeatureAccess({ id: 'ustadh_ai', name: 'Ustadh AI' }, () => {
                        setShowUstadhModal(true);
                      });
                    }
                    setMobileMenuOpen(false);
                    return;
                  }
                  // For Pro features, check access via the subscription upgrade modal
                  if (item.isPro) {
                    const hasAccess = isPro || (item.featureId && isFeatureUnlockedViaAd(item.featureId));
                    if (!hasAccess) {
                      e.preventDefault();
                      requestFeatureAccess(
                        { id: item.featureId || item.label.toLowerCase().replace(/\s+/g, '_'), name: item.label },
                        () => {
                          // On access granted, navigate to the page
                          window.location.href = item.path;
                        }
                      );
                      return;
                    }
                  }
                  setMobileMenuOpen(false);
                };
                
                // Calculate if item is locked for display purposes
                const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));
                const hasProAccess = isPro || (item.featureId && isFeatureUnlockedViaAd(item.featureId));
                
                return (
                  <Link
                    key={item.path}
                    to={item.isUstadh ? '#' : item.path}
                    onClick={handleNavClick}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    className={`group flex items-center gap-4 rounded-xl px-4 py-3.5 transition-all duration-300 animate-fade-in-up stagger-${index + 1} ${emeraldNavClass} ${highlightClass}`}
                    style={{ opacity: 0, animationFillMode: 'forwards' }}
                  >
                    <div className={`rounded-lg p-2 transition-colors ${
                      active 
                        ? (appTheme === 'emerald' ? 'bg-black/20' : 'bg-white/20')
                        : (appTheme === 'emerald' ? 'bg-[#c9a227]/20' : 'bg-muted/50 group-hover:bg-muted')
                    }`}>
                      <Icon className={`h-5 w-5 ${
                        appTheme === 'emerald' && !active 
                          ? 'text-[#c9a227]'  // Gold icons for Emerald theme
                          : item.isUstadh && !active 
                            ? 'text-amber-500' 
                            : item.isHighlight && !active 
                              ? 'text-emerald-500' 
                              : ''
                      }`} />
                    </div>
                    <span className={`font-semibold flex-1 ${appTheme === 'emerald' ? 'text-base' : 'font-medium'}`}>{item.label}</span>
                    {/* PRO badge for Pro-only features (only show if locked) */}
                    {isItemLocked && (
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white flex items-center gap-0.5">
                        <Crown className="h-2.5 w-2.5" />
                        PRO
                      </span>
                    )}
                    {/* NEW badge for Halal Scanner */}
                    {item.isHighlight && hasProAccess && (
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-500 text-white">
                        NEW
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Theme Toggle - Hidden on Desktop (moved to header) */}
            <div className={`border-t p-4 space-y-2 lg:hidden ${appTheme === 'emerald' ? 'border-[#c9a227]/30' : 'border-border/50'}`}>
              {/* Language Selector */}
              <div className="relative">
                <Button
                  variant="ghost"
                  className={`w-full justify-start gap-4 rounded-xl py-6 ${appTheme === 'emerald' ? 'hover:bg-[#c9a227]/20 text-white/90' : 'hover:bg-muted/50'}`}
                  onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                  data-testid="language-selector"
                >
                  <div className={`rounded-lg p-2 ${appTheme === 'emerald' ? 'bg-white/10' : 'bg-muted/50'}`}>
                    <Globe className={`h-5 w-5 ${appTheme === 'emerald' ? 'text-[#c9a227]' : 'text-emerald-600'}`} />
                  </div>
                  <span className="font-medium flex items-center gap-2">
                    <span>{currentLang.flag}</span>
                    <span>{currentLang.nativeName}</span>
                  </span>
                </Button>
                
                {showLanguageMenu && (
                  <div className="absolute bottom-full left-0 right-0 mb-2 bg-card border border-border rounded-2xl shadow-xl z-50 overflow-hidden language-dropdown">
                    <ScrollArea className="h-64">
                      <div className="p-2">
                        {languages.map((lang) => (
                          <button
                            key={lang.code}
                            onClick={() => changeLanguage(lang.code)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
                              i18n.language === lang.code
                                ? 'bg-primary text-primary-foreground'
                                : 'hover:bg-muted text-foreground'
                            }`}
                          >
                            <span className="text-lg">{lang.flag}</span>
                            <div>
                              <span className="font-medium block">{lang.nativeName}</span>
                              <span className={`text-xs ${i18n.language === lang.code ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>{lang.name}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </div>

              {/* Night Vision Toggle - Only visible for Pro users (STRICT PRO ONLY) */}
              {(isPro || mode === 'dark') && (
                <Button
                  variant="ghost"
                  className={`w-full justify-start gap-4 rounded-xl py-6 ${appTheme === 'emerald' ? 'hover:bg-[#c9a227]/20 text-white/90' : 'hover:bg-muted/50'}`}
                  onClick={handleNightVisionToggle}
                  data-testid="theme-toggle"
                >
                  <div className={`rounded-lg p-2 ${appTheme === 'emerald' ? 'bg-white/10' : 'bg-muted/50'}`}>
                    {theme === 'dark' ? (
                      <Sun className={`h-5 w-5 ${appTheme === 'emerald' ? 'text-[#c9a227]' : 'text-yellow-500'}`} />
                    ) : (
                      <Moon className={`h-5 w-5 ${appTheme === 'emerald' ? 'text-[#c9a227]' : 'text-blue-600'}`} />
                    )}
                  </div>
                  <span className="font-medium">
                    {t('nav.darkMode')}
                  </span>
                </Button>
              )}
            </div>

            {/* Footer */}
            <div className="p-4">
              <div className={`rounded-2xl p-4 border ${appTheme === 'emerald' ? 'bg-[#c9a227]/10 border-[#c9a227]/30' : 'bg-primary/5 border-primary/20'}`}>
                <p className={`text-xs text-center ${
                  ((appTheme === 'classic' || appTheme === 'kids') && mode === 'light')
                    ? 'text-muted-foreground'
                    : 'text-white/70'
                }`}>Tanzeen <span className="font-black" style={{fontWeight: 900, WebkitTextStroke: "1px currentColor"}}>+</span> | {t('app.tagline')}</p>
              </div>
            </div>
          </div>
        </aside>

        {/* Mobile Header - Only shown on Homepage and QuranPage.
            The shared app safe-area band reserves the display cutout before
            this fixed header; no theme owns an additional inset. */}
        {showsMobileHeader && (
        <header
          className={`fixed left-0 right-0 z-50 flex h-16 items-center justify-between border-b px-4 backdrop-blur-xl lg:hidden ${
            appTheme === 'emerald' 
              ? 'bg-[#0A3022]/95 border-[#A16207]/30' 
              : appTheme === 'classic'
                ? (mode === 'dark' ? 'bg-[#1e293b]/95 border-[#C9A227]/40' : 'bg-[#F1EBDD]/95 border-[#C9A227]/40')
                : appTheme === 'kids'
                  ? 'bg-white/95 border-pink-200/80'
                  : 'bg-background/80 border-border/50'
          }`}
          style={{ top: 'var(--app-safe-area-height)' }}
        >
          <Link to="/" className="flex items-center gap-2" data-testid="mobile-logo">
            <img 
              src={((appTheme === 'classic' || appTheme === 'kids') && mode === 'light') 
                ? "/images/tanzeel-logo-black.png" 
                : "/images/tanzeel-logo.png"
              }
              alt="Tanzeen +" 
              className="h-8 w-8 object-contain"
            />
            <div>
              <span className={`font-serif text-lg font-bold ${
                appTheme === 'emerald' ? 'text-white' : appTheme === 'classic' ? 'text-[#0F4C3A]' : 'text-foreground'
              }`}>Tanzeen <span className="font-black" style={{fontWeight: 900, WebkitTextStroke: "1px currentColor"}}>+</span></span>
              <p 
                className={`text-[9px] italic -mt-1 ${
                  (appTheme === 'emerald' || appTheme === 'royal') ? 'beyond-elegance-text' : ''
                }`}
                style={{ 
                  color: appTheme === 'emerald' || appTheme === 'royal' 
                    ? '#ffffff' 
                    : appTheme === 'classic' 
                      ? '#C9A227' 
                      : appTheme === 'kids'
                        ? '#be185d'
                        : undefined 
                }}
              >{t('app.tagline')}</p>
            </div>
          </Link>

          <div className="flex items-center gap-1">
            {/* Show icons on all pages EXCEPT homepage with Royal theme */}
            {!(location.pathname === '/' && appTheme === 'royal') && (
              <>
                {/* Language Toggle with Dropdown */}
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowMobileLanguageMenu(!showMobileLanguageMenu)}
                    className={`rounded-xl h-12 w-12 ${
                      appTheme === 'emerald' ? 'text-[#facc15] hover:bg-[#facc15]/10' : appTheme === 'classic' ? 'text-[#0F4C3A] hover:bg-[#C9A227]/15' : ''
                    }`}
                    data-testid="mobile-language-toggle"
                  >
                    <Globe className="h-5 w-5" />
                  </Button>
                  
                  {/* Language Dropdown Menu */}
                  {showMobileLanguageMenu && (
                    <>
                      {/* Backdrop to close menu */}
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setShowMobileLanguageMenu(false)}
                      />
                      {/* NOTE (2026-02-25): use logical `end-*` (inset-inline-end)
                          instead of physical `right-0` so the dropdown hangs off
                          the correct side of the button in both LTR and RTL.
                          In LTR: dropdown's right edge = button's right edge (hangs left).
                          In RTL: dropdown's left edge = button's left edge (hangs right).
                          This keeps it fully on-screen in Arabic. */}
                      <div
                        className="absolute top-full end-0 mt-2 bg-card border border-border rounded-2xl shadow-xl z-50 w-56 max-h-80 overflow-y-auto overscroll-contain"
                        style={{ WebkitOverflowScrolling: 'touch' }}
                        onClick={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        onTouchMove={(e) => e.stopPropagation()}
                      >
                        <div className="p-2">
                          <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            {t('settings.language')}
                          </div>
                          {languages.map((lang) => (
                            <button
                              key={lang.code}
                              onClick={() => {
                                changeLanguage(lang.code);
                                setShowMobileLanguageMenu(false);
                              }}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
                                i18n.language === lang.code
                                  ? 'bg-primary text-primary-foreground'
                                  : 'hover:bg-muted text-foreground'
                              }`}
                            >
                              <span className="text-lg">{lang.flag}</span>
                              <div className="flex-1 min-w-0">
                                <span className="font-medium block truncate">{lang.nativeName}</span>
                                <span className={`text-xs ${i18n.language === lang.code ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>{lang.name}</span>
                              </div>
                              {i18n.language === lang.code && (
                                <span className="text-primary-foreground">✓</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Theme Selector Button (Palette) - replaces Settings */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setThemeDialogOpen(true)}
                  className={`rounded-xl h-12 w-12 ${
                    appTheme === 'emerald' ? 'text-[#facc15] hover:bg-[#facc15]/10' : appTheme === 'classic' ? 'text-[#0F4C3A] hover:bg-[#C9A227]/15' : ''
                  }`}
                  data-testid="mobile-theme-selector-button"
                >
                  <Palette className="h-5 w-5" />
                </Button>
                
                {/* Theme Toggle - Only visible for Pro users or if already in dark mode (STRICT PRO ONLY) */}
                {(isPro || mode === 'dark') && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleNightVisionToggle}
                    className={`rounded-xl h-12 w-12 ${
                      appTheme === 'emerald' ? 'text-[#facc15] hover:bg-[#facc15]/10' : appTheme === 'classic' ? 'text-[#0F4C3A] hover:bg-[#C9A227]/15' : ''
                    }`}
                    data-testid="mobile-theme-toggle"
                  >
                    {theme === 'dark' ? <Sun className="h-6 w-6" /> : <Moon className="h-6 w-6" />}
                  </Button>
                )}
              </>
            )}

            {/* Hamburger Menu - Show for hamburger style, OR as a safety fallback when
                the user selected 'fab' / 'bottom' but their Pro entitlement is not (yet)
                loaded / has been revoked, otherwise they'd be left with no nav at all. */}
            {(() => {
              const style = settings.floatingMenuStyle;
              const usingHamburger = !style || style === 'hamburger';
              const needsProAccess = style === 'fab' || style === 'bottom';
              const hasNavAccess = isPro || isFeatureUnlockedViaAd('bottom_nav');
              const showHamburger = usingHamburger || (needsProAccess && !hasNavAccess);
              return showHamburger;
            })() && (
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="default" size="icon" className={`rounded-xl h-12 w-12 ${appTheme === 'classic' ? 'bg-[#0F4C3A] hover:bg-[#125845] text-[#C9A227]' : 'bg-primary hover:bg-primary/90'}`} data-testid="mobile-menu-button">
                  <Menu className="h-7 w-7" />
                </Button>
              </SheetTrigger>
              
              {/* Royal Knight Theme - Special elegant menu */}
              {appTheme === 'royal' ? (
                <SheetContent side="right" className="w-80 p-0 border-0 !bg-transparent [&>button]:text-white [&>button]:opacity-100" style={{ background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)' }}>
                  <div className="flex h-full flex-col">
                    {/* Royal Knight Header */}
                    <div className="p-6" style={{ background: 'linear-gradient(135deg, #2a2a4a 0%, #1e1e3a 100%)', borderBottom: '2px solid rgba(201, 162, 39, 0.4)' }}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <img src="/images/tanzeel-logo.png" alt="Tanzeen +" className="h-10 w-10 object-contain" />
                          <div>
                            <div style={{ color: '#ffffff', fontSize: '1.25rem', fontWeight: 700, fontFamily: 'serif', letterSpacing: '0.025em', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                              Tanzeen <span style={{ color: '#c9a227', fontWeight: 900 }}>+</span>
                            </div>
                            <div style={{ color: '#ffffff', fontSize: '10px', fontStyle: 'italic', letterSpacing: '0.05em', opacity: 0.9 }}>{t('app.tagline')}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Royal Knight Navigation */}
                    <ScrollArea className="flex-1 py-4">
                      <nav className="space-y-2 px-4">
                        {navItems.map((item) => {
                          const Icon = item.icon;
                          const active = isActive(item.path);
                          const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));
                          
                          // Royal Knight cream card styling
                          const navItemStyle = active 
                            ? {
                                background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 50%, #7c3aed 100%)',
                                boxShadow: '0 4px 20px rgba(139, 92, 246, 0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
                                border: '1px solid rgba(139, 92, 246, 0.5)',
                              }
                            : {
                                background: 'linear-gradient(135deg, #f5f0e6 0%, #ebe5d9 100%)',
                                border: '1px solid rgba(201, 162, 39, 0.4)',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                              };
                          
                          const iconContainerStyle = active 
                            ? {
                                background: 'rgba(255, 255, 255, 0.2)',
                                border: '2px solid rgba(255, 255, 255, 0.4)',
                              }
                            : {
                                background: 'rgba(201, 162, 39, 0.2)',
                                border: '2px solid #b8860b',
                              };
                          
                          const textColor = active ? '#ffffff' : '#2d2a26';
                          const iconColor = active ? '#ffffff' : '#10b981';
                          
                          const handleNavClick = (e) => {
                            if (isItemLocked) {
                              e.preventDefault();
                              setMobileMenuOpen(false);
                              requestFeatureAccess(
                                { id: item.featureId || item.label.toLowerCase().replace(/\s+/g, '_'), name: item.label },
                                () => { window.location.href = item.path; }
                              );
                              return;
                            }
                            setMobileMenuOpen(false);
                          };
                          
                          return (
                            <Link
                              key={item.path}
                              to={isItemLocked ? '#' : item.path}
                              onClick={handleNavClick}
                              data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                              className="flex items-center gap-4 rounded-2xl px-4 py-4 transition-all duration-300 no-underline"
                              style={{...navItemStyle, textDecoration: 'none'}}
                            >
                              <div className="rounded-xl p-2.5 flex-shrink-0" style={iconContainerStyle}>
                                <Icon className="h-5 w-5" style={{ color: iconColor, stroke: iconColor }} strokeWidth={2} />
                              </div>
                              <span 
                                className="flex-1 font-semibold text-base tracking-wide"
                                style={{ color: textColor, WebkitTextFillColor: textColor, textShadow: active ? '0 1px 2px rgba(0,0,0,0.3)' : 'none' }}
                              >
                                {item.label}
                              </span>
                              {isItemLocked && (
                                <span 
                                  className="px-2 py-1 rounded-lg text-[9px] font-bold flex items-center gap-1 flex-shrink-0"
                                  style={{ background: 'linear-gradient(135deg, #c9a227 0%, #d4af37 100%)', color: '#0f0f1a', boxShadow: '0 2px 8px rgba(201, 162, 39, 0.3)' }}
                                >
                                  <Crown className="h-2.5 w-2.5" />
                                  PRO
                                </span>
                              )}
                            </Link>
                          );
                        })}
                      </nav>
                    </ScrollArea>
                  </div>
                </SheetContent>
              ) : appTheme === 'kids' ? (
              /* Kids Paradise - Magical dreamy menu */
              <SheetContent 
                side="right" 
                className="w-80 p-0 border-0 overflow-hidden"
                style={{ background: 'linear-gradient(180deg, #fce7f3 0%, #e9d5ff 30%, #dbeafe 60%, #cffafe 100%)' }}
              >
                <div className="flex h-full flex-col relative">
                  {/* Floating Stars/Sparkles decoration */}
                  <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-8 left-4 text-2xl animate-pulse">✨</div>
                    <div className="absolute top-20 right-8 text-xl animate-bounce" style={{ animationDelay: '0.5s' }}>⭐</div>
                    <div className="absolute top-40 left-8 text-lg animate-pulse" style={{ animationDelay: '1s' }}>🌟</div>
                    <div className="absolute bottom-32 right-4 text-2xl animate-bounce" style={{ animationDelay: '0.3s' }}>✨</div>
                    <div className="absolute bottom-20 left-6 text-xl animate-pulse" style={{ animationDelay: '0.7s' }}>⭐</div>
                  </div>
                  
                  {/* Magical Rainbow Header */}
                  <div 
                    className="p-6 relative"
                    style={{ background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 50%, #06b6d4 100%)' }}
                  >
                    <div className="flex items-center justify-between relative z-10">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <img src="/images/tanzeel-logo.png" alt="Tanzeen +" className="h-10 w-10 object-contain" />
                          <span className="absolute -top-1 -right-1 text-sm">✨</span>
                        </div>
                        <div>
                          <span className="font-serif text-xl font-bold text-white drop-shadow-lg">
                            Tanzeen <span className="font-black" style={{fontWeight: 900}}>+</span>
                          </span>
                          <p className="text-xs text-white/90 italic">{t('app.tagline', 'Beyond Elegance')}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setMobileMenuOpen(false)}
                        className="rounded-full h-10 w-10 text-white hover:bg-white/20 backdrop-blur-sm"
                      >
                        <X className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Magical Navigation */}
                  <ScrollArea className="flex-1 py-4 relative z-10">
                    <nav className="space-y-2 px-4">
                      {navItems.map((item, index) => {
                        const Icon = item.icon;
                        const active = isActive(item.path);
                        const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));
                        
                        // Magical rainbow colors for each nav item
                        const magicColors = [
                          { bg: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)', shadow: 'rgba(236, 72, 153, 0.4)' }, // Pink
                          { bg: 'linear-gradient(135deg, #a855f7 0%, #c084fc 100%)', shadow: 'rgba(168, 85, 247, 0.4)' }, // Purple
                          { bg: 'linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)', shadow: 'rgba(6, 182, 212, 0.4)' }, // Cyan
                          { bg: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', shadow: 'rgba(245, 158, 11, 0.4)' }, // Amber
                          { bg: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)', shadow: 'rgba(16, 185, 129, 0.4)' }, // Green
                          { bg: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)', shadow: 'rgba(59, 130, 246, 0.4)' }, // Blue
                          { bg: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)', shadow: 'rgba(239, 68, 68, 0.4)' }, // Red
                          { bg: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)', shadow: 'rgba(139, 92, 246, 0.4)' }, // Violet
                        ];
                        const colorScheme = magicColors[index % magicColors.length];
                        
                        const handleNavClick = (e) => {
                          if (isItemLocked) {
                            e.preventDefault();
                            setMobileMenuOpen(false);
                            requestFeatureAccess(
                              { id: item.featureId || item.label.toLowerCase().replace(/\s+/g, '_'), name: item.label },
                              () => { window.location.href = item.path; }
                            );
                            return;
                          }
                          setMobileMenuOpen(false);
                        };
                        
                        return (
                          <Link
                            key={item.path}
                            to={isItemLocked ? '#' : item.path}
                            onClick={handleNavClick}
                            data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                            className={`flex items-center gap-4 rounded-2xl px-4 py-3.5 transition-all duration-300 ${
                              active ? '' : 'hover:scale-[1.02]'
                            }`}
                            style={{
                              background: active 
                                ? 'linear-gradient(135deg, #ec4899 0%, #a855f7 50%, #06b6d4 100%)'
                                : 'rgba(255, 255, 255, 0.7)',
                              boxShadow: active 
                                ? '0 4px 20px rgba(168, 85, 247, 0.4), 0 0 30px rgba(236, 72, 153, 0.2)'
                                : '0 2px 8px rgba(0, 0, 0, 0.08)',
                              border: active 
                                ? '2px solid rgba(255, 255, 255, 0.3)'
                                : '2px solid rgba(168, 85, 247, 0.2)',
                              backdropFilter: 'blur(10px)',
                            }}
                          >
                            <div 
                              className="rounded-xl p-2.5 flex-shrink-0"
                              style={{
                                background: active ? 'rgba(255, 255, 255, 0.3)' : colorScheme.bg,
                                boxShadow: active ? 'none' : `0 4px 12px ${colorScheme.shadow}`,
                              }}
                            >
                              <Icon className="h-5 w-5 text-white" strokeWidth={2} />
                            </div>
                            <span 
                              className="flex-1 font-bold text-base"
                              style={{ color: active ? '#ffffff' : '#581c87' }}
                            >
                              {item.kidsLabel || item.label}
                            </span>
                            {isItemLocked && (
                              <span 
                                className="px-2 py-1 rounded-full text-[9px] font-bold flex items-center gap-1"
                                style={{ 
                                  background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)', 
                                  color: '#7c2d12',
                                  boxShadow: '0 2px 8px rgba(251, 191, 36, 0.4)'
                                }}
                              >
                                <Star className="h-2.5 w-2.5 fill-current" />
                                PRO
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </nav>
                  </ScrollArea>
                  
                  {/* Magical Footer */}
                  <div className="p-4 border-t border-purple-200/50 relative z-10">
                    <div 
                      className="rounded-2xl p-4 text-center"
                      style={{ background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)' }}
                    >
                      <p className="text-sm font-medium text-purple-600">
                        ✨ Tanzeen + ✨
                      </p>
                      <p className="text-xs text-purple-400 mt-0.5">{t('app.tagline', 'Beyond Elegance')}</p>
                    </div>
                  </div>
                </div>
              </SheetContent>
              ) : (
              
              /* Other themes - Default menu */
              <SheetContent side="right" className={`w-80 p-0 ${sidebarStyles.container}`}>
                <div className="flex h-full flex-col">
                  {/* Colorful Header */}
                  <div className={`${sidebarStyles.header} p-6`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <img 
                          src={((appTheme === 'classic' || appTheme === 'kids') && mode === 'light') 
                            ? "/images/tanzeel-logo-black.png" 
                            : "/images/tanzeel-logo.png"
                          }
                          alt="Tanzeen +" 
                          className="h-8 w-8 object-contain"
                        />
                        <div className={sidebarStyles.headerText}>
                          <span className="font-serif text-xl font-bold block text-foreground">Tanzeen <span className="font-black" style={{fontWeight: 900, WebkitTextStroke: "1px currentColor"}}>+</span></span>
                          <p className={`text-xs ${sidebarStyles.headerTagline} italic`}>{t('app.tagline')}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setMobileMenuOpen(false)}
                        className={`rounded-xl ${sidebarStyles.headerText} ${appTheme === 'emerald' ? 'hover:bg-black/10' : 'hover:bg-white/20'} h-10 w-10`}
                      >
                        <X className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Navigation with colorful icons */}
                  <nav className="flex-1 space-y-1 p-3 overflow-y-auto" style={{ willChange: 'transform', WebkitOverflowScrolling: 'touch' }}>
                    {navItems.map((item, index) => {
                      const Icon = item.icon;
                      const active = isActive(item.path);
                      // Different colors for each nav item (used for non-emerald themes)
                      const colors = [
                        'from-blue-500 to-blue-600',
                        'from-amber-500 to-orange-500',
                        'from-emerald-500 to-teal-500',
                        'from-pink-500 to-rose-500',
                        'from-purple-500 to-indigo-500',
                        'from-cyan-500 to-sky-500',
                        'from-yellow-500 to-amber-500',
                        'from-slate-500 to-gray-600',
                      ];
                      const iconColorGradient = colors[index % colors.length];
                      
                      // Emerald theme uses gold icons
                      const isEmeraldTheme = appTheme === 'emerald';
                      const isClassicTheme = appTheme === 'classic';
                      
                      // Handle click with PRO gating
                      const handleMobileNavClick = (e) => {
                        if (item.isPro) {
                          const hasAccess = isPro || (item.featureId && isFeatureUnlockedViaAd(item.featureId));
                          if (!hasAccess) {
                            e.preventDefault();
                            setMobileMenuOpen(false);
                            requestFeatureAccess(
                              { id: item.featureId || item.label.toLowerCase().replace(/\s+/g, '_'), name: item.label },
                              () => { window.location.href = item.path; }
                            );
                            return;
                          }
                        }
                        setMobileMenuOpen(false);
                      };
                      
                      const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));
                      
                      return (
                        <Link
                          key={item.path}
                          to={isItemLocked ? '#' : item.path}
                          onClick={handleMobileNavClick}
                          data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                          className={`flex items-center gap-4 rounded-2xl px-4 py-4 transition-all ${
                            active
                              ? sidebarStyles.navLinkActive + ' shadow-lg'
                              : `${sidebarStyles.navLink} ${sidebarStyles.navLinkHover}`
                          }`}
                          style={{ color: appTheme === 'emerald' ? '#ffffff' : undefined }}
                        >
                          <div className={`rounded-xl p-2.5 ${
                            active 
                              ? (isEmeraldTheme ? 'bg-black/10' : isClassicTheme ? 'bg-[#C9A227]/25' : 'bg-white/20')
                              : isEmeraldTheme 
                                ? 'bg-[#c9a227]/20'
                                : isClassicTheme
                                  ? 'bg-[#C9A227]/20 border border-[#C9A227]/40'
                                  : `bg-gradient-to-br ${iconColorGradient} shadow-md`
                          }`}>
                            <Icon 
                              className={`h-5 w-5 ${!isEmeraldTheme && !isClassicTheme && !active ? 'text-white' : ''}`} 
                              style={{ 
                                color: isEmeraldTheme && !active 
                                  ? '#c9a227' 
                                  : isClassicTheme
                                    ? (active ? '#C9A227' : '#0F4C3A')
                                    : undefined 
                              }} 
                            />
                          </div>
                          <span className="font-semibold text-base flex-1" style={{ color: appTheme === 'emerald' ? '#ffffff' : undefined }}>
                            {appTheme === 'kids' && item.kidsLabel ? item.kidsLabel : item.label}
                          </span>
                          {isItemLocked && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white flex items-center gap-0.5">
                              <Crown className="h-2.5 w-2.5" />
                              PRO
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </nav>
                  
                  {/* Footer */}
                  <div className="p-4 border-t border-border/30">
                    <div className={`rounded-2xl ${sidebarStyles.footer} p-4 text-center`}>
                      <p className={`text-xs ${
                        ((appTheme === 'classic' || appTheme === 'kids') && mode === 'light')
                          ? 'text-muted-foreground'
                          : 'text-white/70'
                      }`}>Tanzeen <span className="font-black" style={{fontWeight: 900, WebkitTextStroke: "1px currentColor"}}>+</span> | {t('app.tagline')}</p>
                    </div>
                  </div>
                </div>
              </SheetContent>
              )}
            </Sheet>
            )}
          </div>
        </header>
        )}

        {/* Main Content - the shared band shifts all routes equally; only home
            and Quran clear the fixed 4rem mobile header. */}
        <main
          className={`min-h-screen lg:ml-72 lg:pt-0 ${
            appTheme === 'classic' ? 'bg-[#F1EBDD]' : ''
          } ${
            showsMobileHeader
              ? 'pt-16'
              : 'pt-0'
          }`}
        >
          <Outlet />
        </main>

        {/* Ad Manager - Handles all ad placements (no banner, just interstitials) */}
        <AdManager />

        {/* Premium Floating Dock (Mobile) - Only show when 'bottom' style selected */}
        {/* Hidden on Halal Scanner page since it has its own bottom tab bar */}
        {(isPro || isFeatureUnlockedViaAd('bottom_nav')) && settings.floatingMenuStyle === 'bottom' && location.pathname !== '/halal-scanner' && !location.pathname.startsWith('/halal-scanner') && (
          <nav className="floating-dock lg:hidden">
            <div 
              className="flex items-center overflow-x-auto scrollbar-hide snap-x snap-mandatory"
              style={{ 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
                maxWidth: '280px', // Limit width to show ~5 icons
                margin: '0 auto',
                paddingLeft: '8px', // Prevent left icon from being cut off
                paddingRight: '8px', // Balanced padding on both sides
              }}
            >
              {/* All features using shared navigation config - Tajweed Coach below Masjid Guide */}
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));
                
                const handleDockClick = (e) => {
                  if (isItemLocked) {
                    e.preventDefault();
                    requestFeatureAccess(
                      { id: item.featureId, name: item.label },
                      () => { navigate(item.path); }
                    );
                  }
                };
                
                // Kids theme uses Star icon for PRO badge, others use Crown
                const ProBadgeIcon = appTheme === 'kids' ? Star : Crown;
                
                return (
                  <Tooltip key={item.id || item.path}>
                    <TooltipTrigger asChild>
                      <Link
                        to={isItemLocked ? '#' : item.path}
                        onClick={handleDockClick}
                        data-testid={`dock-nav-${item.id || item.label.toLowerCase().replace(' ', '-')}`}
                        className={`flex-shrink-0 flex h-12 w-12 items-center justify-center rounded-full transition-all duration-300 snap-center relative ${
                          active 
                            ? 'bg-primary text-primary-foreground shadow-lg scale-110' 
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        }`}
                      >
                        <Icon className={`h-6 w-6 ${active ? 'stroke-[2.5]' : ''}`} />
                        {isItemLocked && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                            <ProBadgeIcon className={`h-2.5 w-2.5 text-white ${appTheme === 'kids' ? 'fill-white' : ''}`} />
                          </span>
                        )}
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="rounded-lg">
                      {item.label} {isItemLocked && '(PRO)'}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </nav>
        )}
        
        {/* Floating Action Button - Only show when 'fab' style selected */}
        {/* Hidden on Halal Scanner page since it has its own bottom tab bar */}
        {(isPro || isFeatureUnlockedViaAd('bottom_nav')) && settings.floatingMenuStyle === 'fab' && location.pathname !== '/halal-scanner' && !location.pathname.startsWith('/halal-scanner') && (
          <FloatingActionButton />
        )}
        
        {/* Theme Selector Dialog - Matching QuranPage Design */}
        <Dialog open={themeDialogOpen} onOpenChange={setThemeDialogOpen}>
          <DialogContent className="sm:max-w-md" style={{ background: '#ffffff', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
            <DialogHeader>
              <DialogTitle className="text-center font-serif text-xl" style={{ color: '#059669' }}>
                {t('settings.selectTheme', 'Select Theme')}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-4">
              {Object.entries(themes || {}).map(([key, theme]) => {
                const isSelected = appTheme === key;
                const isFreeTheme = key === 'classic';
                const isLocked = !isPro && !isFreeTheme;
                return (
                  <button
                    key={key}
                    onClick={() => {
                      if (isLocked) {
                        requestFeatureAccess({ 
                          id: 'all_themes', 
                          name: 'Premium Themes',
                          noAdUnlock: true 
                        }, () => {
                          setAppTheme(key);
                          setThemeDialogOpen(false);
                        });
                      } else {
                        setAppTheme(key);
                        setThemeDialogOpen(false);
                      }
                    }}
                    data-testid={`theme-option-${key}`}
                    className={`relative text-left rounded-xl border-2 overflow-hidden transition-all ${
                      isSelected
                        ? 'border-emerald-500 shadow-lg ring-2 ring-emerald-500/20'
                        : isLocked 
                          ? 'border-gray-200 opacity-70'
                          : 'border-gray-200 hover:border-emerald-500/50'
                    }`}
                  >
                    {/* Color preview bars */}
                    <div className="h-12 flex">
                      <div className="flex-1" style={{ backgroundColor: theme.preview?.background || '#1a1a1a' }} />
                      <div className="w-4" style={{ backgroundColor: theme.preview?.primary || '#333' }} />
                      <div className="w-4" style={{ backgroundColor: theme.preview?.secondary || '#666' }} />
                    </div>
                    {/* Theme name section - cream background */}
                    <div className="p-2 bg-[#f5f0e6]">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-semibold text-black">{theme.name}</p>
                          <p className="text-[10px] text-gray-700">{theme.description}</p>
                        </div>
                        {isLocked && (
                          <span className="px-1.5 py-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 text-white text-[9px] font-bold">
                            PRO
                          </span>
                        )}
                        {isFreeTheme && !isPro && (
                          <span className="px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-700 text-[9px] font-medium">
                            FREE
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Checkmark for selected */}
                    {isSelected && (
                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
        
      </div>
    </TooltipProvider>
  );
};

export default Layout;
